import './Cart.scss';

import type { FC } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  RemoveFontLicenseFromCurrentOrderMutation,
  RemoveFontLicenseFromCurrentOrderMutationVariables,
} from 'client/graphql/types/operations';

import useLanguage from 'utils/useLanguage';
import * as Core from 'components/core';
import useCurrentOrderQuery, {
  CURRENT_ORDER_FRAGMENT,
} from 'queries/useCurrentOrderQuery';
import formatCurrency from 'utils/formatCurrency';
import formatMultilineString from 'utils/formatMultilineString';
import ColorBar from 'components/ColorBar';

import { formatDecimal } from 'config/formatting';

const REMOVE_FONT_LICENSE_FROM_CURRENT_ORDER_MUTATION = gql`
  mutation RemoveFontLicenseFromCurrentOrderMutation(
    $input: RemoveFontLicenseFromCurrentOrderInput!
    $language: Language!
  ) {
    removeFontLicenseFromCurrentOrder(input: $input) {
      order {
        ...CurrentOrderFragment
      }

      errors {
        attribute
        message
      }
    }
  }

  ${CURRENT_ORDER_FRAGMENT}
`;

interface Props {}

export const Cart: FC<Props> = () => {
  const { t } = useTranslation();

  const language = useLanguage();

  const { data, loading } = useCurrentOrderQuery({
    fetchPolicy: 'cache-and-network',
  });

  const [removeFontLicenseFromCurrentOrder, { loading: removing }] =
    useMutation<
      RemoveFontLicenseFromCurrentOrderMutation,
      RemoveFontLicenseFromCurrentOrderMutationVariables
    >(REMOVE_FONT_LICENSE_FROM_CURRENT_ORDER_MUTATION);

  if (loading) {
    return (
      <ColorBar>
        <Core.Loader />
      </ColorBar>
    );
  }

  if (
    data?.currentOrder == null ||
    data.currentOrder.fontLicenses.length === 0
  ) {
    return (
      <ColorBar>
        <div className="CheckoutCart is-empty content-wrapper">
          {t('checkoutCart.emptyMessage')}
        </div>
      </ColorBar>
    );
  }

  return (
    <ColorBar>
      <div className="CheckoutCart content-wrapper">
        <h1>{t('checkoutCart.heading')}</h1>

        <div className="CheckoutCart-table">
          <div className="CheckoutCart-table-header">
            <div className="CheckoutCart-table-header-col">
              {t('order.font')}
            </div>

            <div className="CheckoutCart-table-header-col">
              {t('order.styles')}
            </div>

            <div className="CheckoutCart-table-header-col">
              {t('order.licenseType')}
            </div>

            <div className="CheckoutCart-table-header-col">
              {t('order.licenseDetails')}
            </div>

            <div className="CheckoutCart-table-header-col">
              {t('order.price')}
            </div>

            <div className="CheckoutCart-table-header-col" />
          </div>

          {data.currentOrder.fontLicenses.map((fontLicense) => {
            const { family, licenseOption } = fontLicense;

            if (family == null || licenseOption == null) {
              return null;
            }

            return (
              <div key={fontLicense.id} className="CheckoutCart-table-row">
                <div className="CheckoutCart-table-row-col">
                  <div className="CheckoutCart-table-row-col-mobile-heading">
                    {t('order.font')}
                  </div>
                  {family.name}
                </div>

                <div className="CheckoutCart-table-row-col">
                  <div className="CheckoutCart-table-row-col-mobile-heading">
                    {t('order.styles')}
                  </div>
                  {fontLicense.faces.length}
                </div>

                <div className="CheckoutCart-table-row-col">
                  <div className="CheckoutCart-table-row-col-mobile-heading">
                    {t('order.licenseType')}
                  </div>
                  {t(`licenseType.${licenseOption.type.toLowerCase()}.name`)}
                </div>

                <div className="CheckoutCart-table-row-col">
                  <div className="CheckoutCart-table-row-col-mobile-heading">
                    {t('order.licenseDetails')}
                  </div>

                  <Core.Link
                    to={`/licensing#${licenseOption.type.toLowerCase()}`}
                    target="_blank"
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: formatMultilineString(
                          t(
                            `licenseType.${licenseOption.type.toLowerCase()}.description`,
                            {
                              count: licenseOption.maxValue,
                              description: formatDecimal(
                                language,
                                licenseOption.maxValue,
                              ),
                            },
                          ),
                        ),
                      }}
                    />
                  </Core.Link>
                </div>

                <div className="CheckoutCart-table-row-col">
                  <div className="CheckoutCart-table-row-col-mobile-heading">
                    {t('order.price')}
                  </div>
                  {formatCurrency(fontLicense.total)}
                </div>

                <div className="CheckoutCart-table-row-col">
                  <button
                    className="CheckoutCart-table-row-col-remove-link"
                    disabled={removing}
                    onClick={() => {
                      const fontLicenseId = fontLicense.id;

                      removeFontLicenseFromCurrentOrder({
                        variables: {
                          input: {
                            id: fontLicenseId,
                          },
                          language,
                        },
                        optimisticResponse: {
                          __typename: 'Mutation',
                          removeFontLicenseFromCurrentOrder: {
                            __typename:
                              'RemoveFontLicenseFromCurrentOrderPayload',
                            order:
                              data.currentOrder != null
                                ? {
                                    ...data.currentOrder,
                                    fontLicenses:
                                      data.currentOrder.fontLicenses.filter(
                                        (fontLicense) =>
                                          fontLicense.id !== fontLicenseId,
                                      ),
                                  }
                                : null,
                            errors: [],
                          },
                        },
                      });
                    }}
                  >
                    {t('checkoutCart.remove')}
                  </button>
                </div>
              </div>
            );
          })}

          <div className="CheckoutCart-table-footer">
            <div className="CheckoutCart-table-footer-label">
              {t('order.subtotal')}
            </div>

            <div className="CheckoutCart-table-footer-col" />
            <div className="CheckoutCart-table-footer-col" />
            <div className="CheckoutCart-table-footer-col" />

            <div className="CheckoutCart-table-footer-total">
              {formatCurrency(
                data.currentOrder.fontLicenses.reduce(
                  (sum, fontLicense) => sum + fontLicense.total,
                  0,
                ),
              )}
            </div>

            <div className="CheckoutCart-table-footer-col" />
          </div>
        </div>

        <div className="CheckoutCart-button-wrapper">
          <Core.Link
            to="/checkout/payment"
            button
            className="CheckoutCart-button"
            testID="checkout-button"
          >
            {t('checkoutCart.checkOut')}
          </Core.Link>
        </div>
      </div>
    </ColorBar>
  );
};

Cart.displayName = 'Checkout.Cart';

export default Cart;
